import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { from, Observable, switchMap } from "rxjs";
import { Utils } from "../util/utils";
import { environment } from "src/environments/environment";

/**
 * Apply compression of select requests.
 * Currently used on request which payload contains a fi.
 * This is an experimental feature aiming to aleviate the performance cost
 * that comes with the coreruleset WAF filtering.
 * This performance cost can be huge with large request payloads.
 * We measured an overhead of 4s+ on some fi which make heavy use of the field override feature
 */
@Injectable()
export class CompressionInterceptor implements HttpInterceptor {
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!environment.enableCompression) return next.handle(request);

    // Apply compression on select fi properties
    if (request.body?.lastModifiedDateSeconds) {
      return this.compressFi(request, next, request.body);
    } else if (request.body?.formInstanceDto) {
      return this.compressFi(request, next, request.body.formInstanceDto);
    }
    // Everything else: no compression required
    else {
      return next.handle(request);
    }
  }

  /**
   * Form instance compression:
   * - compressedProperties: values and fieldUpdates properties (compressed and encoded as base64)
   * - values: set to null
   * - fieldUpdates: set to null
   * - dynamicFieldOverrides: set to null (should not be part of a pushed fi)
   * - comboboxFilterUpdates: same
   *
   * @param request
   * @param next
   * @param fiBody
   * @returns
   */
  private compressFi(
    request: HttpRequest<any>,
    next: HttpHandler,
    fiBody: any,
  ): Observable<HttpEvent<any>> {
    if (fiBody?.compressedProperties) {
      return next.handle(request);
    }

    const compressionProperties = {
      values: fiBody.values,
      fieldUpdates: fiBody.fieldUpdates,
    };

    return from(Utils.compress(JSON.stringify(compressionProperties))).pipe(
      switchMap((compressedProperties) => {
        const compressedPropertiesAsString = btoa(
          String.fromCharCode(...new Uint8Array(compressedProperties)),
        );
        fiBody.compressedProperties = compressedPropertiesAsString;

        // Compressed properties
        fiBody.values = null;
        fiBody.fieldUpdates = null;

        // Void properties
        fiBody.dynamicFieldOverrides = null;
        fiBody.comboboxFilterUpdates = null;

        return next.handle(request);
      }),
    );
  }
}
